import React, { useState, useEffect, useMemo } from "react";
import { FaEllipsisV } from 'react-icons/fa';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter, faUser, faStar as fasStar } from "@fortawesome/free-solid-svg-icons";
import { faStar as farStar } from "@fortawesome/free-regular-svg-icons";
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from "react-router-dom";
import { url } from "../../data/url";

const Candidates = () => {
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const [selectedPositions, setSelectedPositions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRatings, setSelectedRatings] = useState([]);
  const [sortBy, setSortBy] = useState("Last Name (A-Z)");
  const [activeNavItem, setActiveNavItem] = useState('All');

  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${url.path}/employer/candidates`, {
          withCredentials: true
        });
        setCandidates(response.data);
        setLoading(false);
      } catch (err) {
        if(err.response && err.response.status === 401) {
          navigate('/login');
        }
        else if (err.response && err.response.status === 404) {
          setCandidates([]);
        } else {
          setError('Failed to fetch candidates');
        }
        setLoading(false);
      }
    };

    fetchCandidates();
  }, []);

  const toggleDropdown = (jobId) => {
    setActiveDropdown(activeDropdown === jobId ? null : jobId);
  };

  const positions = useMemo(() => {
    const allPositions = [...new Set(candidates.map((c) => c.position))];
    return allPositions.slice(0, 9).concat("Other");
  }, [candidates]);

  const filteredCandidates = useMemo(() => {
    return candidates.filter(
      (candidate) =>
        (selectedPositions.length === 0 ||
          selectedPositions.includes(candidate.position)) &&
        candidate.position.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (selectedRatings.length === 0 ||
          selectedRatings.some((range) => {
            const [min, max] = range.split(" - ").map(parseFloat);
            return candidate.rating >= min && candidate.rating <= max;
          })) &&
        (activeNavItem === 'All' ||
         (activeNavItem === 'inReview' && candidate.selected === 'no' && !candidate.rejected) ||
         (activeNavItem === 'Hired' && candidate.hired) ||
         (activeNavItem === 'Saved' && candidate.saved) ||
         (activeNavItem === 'Rejected' && candidate.rejected))
    );
  }, [candidates, selectedPositions, searchTerm, selectedRatings, activeNavItem]);

  const sortedCandidates = useMemo(() => {
    return [...filteredCandidates].sort((a, b) => {
      switch (sortBy) {
        case "Last Name (A-Z)":
          return a.name.split(" ").pop().localeCompare(b.name.split(" ").pop());
        case "Last Name (Z-A)":
          return b.name.split(" ").pop().localeCompare(a.name.split(" ").pop());
        case "First Name (A-Z)":
          return a.name.localeCompare(b.name);
        case "First Name (Z-A)":
          return b.name.localeCompare(a.name);
        case "Stars (High-Low)":
          return b.rating - a.rating;
        case "Stars (Low-High)":
          return a.rating - b.rating;
        case "Date (Newest-Oldest)":
          return new Date(b.date) - new Date(a.date);
        case "Date (Oldest-Newest)":
          return new Date(a.date) - new Date(b.date);
        default:
          return 0;
      }
    });
  }, [filteredCandidates, sortBy]);

  const handlePositionChange = (position) => {
    setSelectedPositions((prev) =>
      prev.includes(position)
        ? prev.filter((p) => p !== position)
        : [...prev, position]
    );
  };

  const handleRatingChange = (rating) => {
    setSelectedRatings((prev) =>
      prev.includes(rating)
        ? prev.filter((r) => r !== rating)
        : [...prev, rating]
    );
  };

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <FontAwesomeIcon
          key={i}
          icon={i <= rating ? fasStar : farStar}
          className={i <= rating ? "star-filled" : "star-empty"}
        />
      );
    }
    return stars;
  };

  const handleScheduleMeeting = async (candidate) => {
    const { value: formValues } = await Swal.fire({
      title: 'Schedule a Meeting',
      html:
        '<input id="swal-input1" class="swal2-input" placeholder="Meeting Link">' +
        '<input id="swal-input2" class="swal2-input" type="date">' +
        '<input id="swal-input3" class="swal2-input" type="time">',
      focusConfirm: false,
      preConfirm: () => {
        return [
          document.getElementById('swal-input1').value,
          document.getElementById('swal-input2').value,
          document.getElementById('swal-input3').value
        ]
      }
    });

    if (formValues) {
      const [meetingLink, date, time] = formValues;
      try {
        await axios.post(`${url.path}/employer/candidates/schedule-interview`, {
          studentID: candidate.id,
          studentName: candidate.name,
          date,
          time,
          jobTitle: candidate.position,
          meetingLink
        }, { withCredentials: true });
        Swal.fire('Success', 'Interview scheduled successfully', 'success');
      } catch (error) {
        console.error('Error scheduling interview:', error);
        Swal.fire('Error', 'Failed to schedule interview', 'error');
      }
    }
  };

  const handleHire = async (candidate) => {
    if (candidate.hired) return;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to hire this candidate?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, hire them!'
    });

    if (result.isConfirmed) {
      try {
        await axios.post(`${url.path}/employer/candidates/hire-candidate`, {
          studentID: candidate.id,
          jobID: candidate.jobID 
        }, { withCredentials: true });
        Swal.fire('Hired!', 'The candidate has been hired.', 'success');
        // Update local state
        setCandidates(prevCandidates => 
          prevCandidates.map(c => 
            c.id === candidate.id ? {...c, hired: true, selected: 'yes'} : c
          )
        );
      } catch (error) {
        console.error('Error hiring candidate:', error);
        Swal.fire('Error', 'Failed to hire candidate', 'error');
      }
    }
  };

  const handleSave = async (candidate) => {
    try {
      if (candidate.saved) {
        await axios.post(`${url.path}/employer/candidates/unsave-candidate`, {
          studentID: candidate.id
        }, { withCredentials: true });
        Swal.fire('Unsaved', 'The candidate has been removed from saved list.', 'success');
      } else {
        await axios.post(`${url.path}/employer/candidates/save-candidate`, {
          studentID: candidate.id
        }, { withCredentials: true });
        Swal.fire('Saved', 'The candidate has been added to saved list.', 'success');
      }
      // Update local state
      setCandidates(prevCandidates => 
        prevCandidates.map(c => 
          c.id === candidate.id ? {...c, saved: !c.saved} : c
        )
      );
    } catch (error) {
      console.error('Error saving/unsaving candidate:', error);
      Swal.fire('Error', 'Failed to save/unsave candidate', 'error');
    }
  };

  const handleReject = async (candidate) => {
    if (candidate.rejected) return;

    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'Do you want to reject this candidate?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, reject them'
    });

    if (result.isConfirmed) {
      try {
        await axios.post(`${url.path}/employer/candidates/reject-candidate`, {
          studentID: candidate.id
        }, { withCredentials: true });
        Swal.fire('Rejected', 'The candidate has been rejected.', 'success');
        // Update local state
        setCandidates(prevCandidates => 
          prevCandidates.map(c => 
            c.id === candidate.id ? {...c, rejected: true} : c
          )
        );
      } catch (error) {
        console.error('Error rejecting candidate:', error);
        Swal.fire('Error', 'Failed to reject candidate', 'error');
      }
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="candidates-page">
      {candidates.length >= 10 && (
        <div className="sidebar">
          <div className="filter-header">
            <FontAwesomeIcon icon={faFilter} />
            <span>Filter candidates by</span>
          </div>
          <div className="filter-section">
            <h3>Title</h3>
            {positions.map((position) => (
              <label key={position} className="checkbox-label">
                <input
                  type="checkbox"
                  checked={selectedPositions.includes(position)}
                  onChange={() => handlePositionChange(position)}
                />
                {position}
              </label>
            ))}
            <input
              type="text"
              placeholder="Search positions..."
              onChange={(e) => setSearchTerm(e.target.value)}
              className="position-search"
            />
          </div>
          <div className="filter-section">
            <h3>Overall Rating</h3>
            {[
              "4 stars - 5 stars",
              "3 stars - 4 stars",
              "2 stars - 3 stars",
              "1 star - 2 stars",
              "Not Rated",
            ].map((rating) => (
              <label key={rating} className="checkbox-label">
                <div><input
                  type="checkbox"
                  checked={selectedRatings.includes(rating)}
                  onChange={() => handleRatingChange(rating)}
                />
                </div>
                <div className="rating-container">
                {rating}
                </div>
              </label>
            ))}
          </div>
        </div>
      )}
      <div className="main-content">
        <div className="candidates-header">
          <h2>Candidates ({sortedCandidates.length})</h2>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option>Last Name (A-Z)</option>
            <option>Last Name (Z-A)</option>
            <option>First Name (A-Z)</option>
            <option>First Name (Z-A)</option>
            <option>Stars (High-Low)</option>
            <option>Stars (Low-High)</option>
            <option>Date (Newest-Oldest)</option>
            <option>Date (Oldest-Newest)</option>
          </select>
        </div>
        <nav>
          <ul>
            {['All', 'inReview', 'Hired', 'Saved', 'Rejected'].map(item => (
              <li 
                key={item}
                className={activeNavItem === item ? 'active' : ''}
                onClick={() => setActiveNavItem(item)}
              >
                {item === 'inReview' ? 'In Review' : item}
              </li>
            ))}
          </ul>
        </nav>
        {candidates.length === 0 ? (
          <div className="no-candidates-placeholder">
            No candidates to view for now
          </div>
        ) : (
        <div className="candidates-table-contanier">
        <table className="candidates-table">
            <thead>
              <tr>
                <th>Candidate Name</th>
                <th>Position</th>
                <th>Overall Rating</th>
                <th>Joining Date</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedCandidates.map((candidate) => (
                <tr key={candidate.id}>
                  <td>
                    <div className="candidate-info">
                      <div className="candidate-icon">
                        <FontAwesomeIcon icon={faUser} />
                      </div>
                      <div>{candidate.name}</div>
                    </div>
                  </td>
                  <td>{candidate.position}</td>
                  <td>{renderStars(candidate.rating)}</td>
                  <td>{new Date(candidate.date).toLocaleDateString()}</td>
                  <td>
                    <div
                      className="dropdown"
                      onMouseLeave={() => setActiveDropdown(null)}
                    >
                      <button
                        className="manage-button"
                        onClick={() => toggleDropdown(candidate.id)}
                      >
                        <FaEllipsisV />
                      </button>
                      {activeDropdown === candidate.id && (
                         <div className="dropdown-menu upward">
                         <button className="dropdown-item" onClick={() => handleScheduleMeeting(candidate)}>
                           Schedule a Meeting
                         </button>
                         {!candidate.hired && (
                           <button className="dropdown-item" onClick={() => handleHire(candidate)}>
                             Hire
                           </button>
                         )}
                         <button className="dropdown-item" onClick={() => handleSave(candidate)}>
                           {candidate.saved ? 'Unsave' : 'Save'}
                         </button>
                         {!candidate.rejected && (
                           <button className="dropdown-item" onClick={() => handleReject(candidate)}>
                             Reject
                           </button>
                         )}
                       </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>
    </div>
  );
};

export default Candidates;